import { Box, Typography } from '@mui/material'
import { FormikProps, useFormik } from 'formik'
import * as yup from 'yup'
import RegisterFormActions from '~/src/components/auth/register/RegisterFormActions'
import RegisterFormFields from '~/src/components/auth/register/RegisterFormFields'
import FormError from '~/src/components/error/FormError'
import Link from '~/src/components/mui-wrappers/Link'
import { useTranslations } from '~/src/hooks/useTranslations'
import routes from '~/src/router/enums/routes.enum'
import { RegisterFormInterface } from '~/src/types/forms/RegisterFormInterface'
import { useEoState, useEoValue } from '~/src/hooks/useEoState'
import { LANGUAGE_STATE } from '~/src/stores/lang'
import ValidatorUtils from '~/src/utils/ValidatorUtils'
import AUTHENTICATION_FLOW_STATE, {
  AuthenticationFlowState
} from '~/src/stores/authentication-flow'
import useAuthenticationFlowDialogs from '~/src/hooks/auth/flow/useAuthenticationFlowDialogs'
import { useEffect } from 'react'

interface Props {
  fieldsColor?: 'container' | 'content',
}

const RegisterForm = ({ fieldsColor = 'content' }: Props) => {
  const { next } = useAuthenticationFlowDialogs()
  const [ authenticationFlowState, setAuthenticationFlowState ] = useEoState<AuthenticationFlowState>(AUTHENTICATION_FLOW_STATE)
  const { t } = useTranslations()
  const locale = useEoValue(LANGUAGE_STATE)

  const formik: FormikProps<RegisterFormInterface> = useFormik<RegisterFormInterface>({
    initialValues: {
      email: authenticationFlowState?.customer?.email || '',
      firstName: authenticationFlowState?.customer?.firstName || '',
      lastName: authenticationFlowState?.customer?.lastName || '',
      password: authenticationFlowState?.customer?.password || '',
      phone: authenticationFlowState?.customer?.phone || '',
      isCompany: !!authenticationFlowState?.customer?.company,
      companyName: authenticationFlowState?.customer?.company || '',
      companyVat: authenticationFlowState?.customer?.vatNumber || ''
    },
    validationSchema: yup.object({
      email: yup
        .string()
        .email(t('errors.emailInvalid'))
        .required(t('errors.emailRequired')),
      firstName: yup
        .string()
        .required(t('errors.firstNameRequired')),
      lastName: yup
        .string()
        .required(t('errors.lastNameRequired')),
      password: yup
        .string()
        .required(t('errors.passwordRequired')),
      phone: yup
        .string()
        .test('isNumberValid', t('errors.phoneWrongFormat'), ValidatorUtils.isPhoneNumberValid)
        .required(t('errors.phoneRequired')),
      isCompany: yup
        .boolean()
        .required(),
      companyName: yup
        .string().when('isCompany', {
          is: true,
          then: yup
            .string()
            .required(t('errors.companyRequired')),
          otherwise: yup.string()
        }),
      companyVat: yup
        .string().when('isCompany', {
          is: true,
          then: yup
            .string()
            .required(t('errors.vatRequired')),
          otherwise: yup.string()
        })
    }),
    onSubmit: () => {
      next()
    }
  })

  useEffect(() => {
    setAuthenticationFlowState((state: AuthenticationFlowState) => ({
      ...state,
      customer: {
        ...state.customer,
        email: formik.values.email,
        firstName: formik.values.firstName,
        lastName: formik.values.lastName,
        password: formik.values.password,
        phone: formik.values.phone,
        company: formik.values.companyName,
        vatNumber: formik.values.companyVat,
        locale
      }
    }))
  }, [ formik.values, locale ])

  return (
    <>
      {authenticationFlowState.errors && <FormError errors={authenticationFlowState.errors}/>}

      <form onSubmit={formik.handleSubmit}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            mt: 2
          }}
        >
          <RegisterFormFields
            form={formik}
            fieldsColor={fieldsColor} />

          <Typography>
            {t('auth.byContinuingYouAgreeTo')}
            <Link
              href={routes.PRIVACY_POLICY}
              sx={{
                mx: 0.5
              }}
            >
              {t('auth.privacyPolicy')}
            </Link>
            {t('auth.andOur')}
            <Link
              href={routes.TERMS_AND_CONDITIONS}
              sx={{
                mx: 0.5
              }}
            >
              {t('auth.termsAndConditions')}
            </Link>
          </Typography>

          <RegisterFormActions
            loading={authenticationFlowState.isLoading}
            form={formik}
          />
        </Box>
      </form>
    </>
  )
}

export default RegisterForm
